import { Link as ChakraLink } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Flex,
  Menu as ChakraMenu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Text,
} from '@chakra-ui/react';

import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import TransactionListHeader from './TransactionHeader';
import OutgoingTransactionItem from './OutgoingTransactionItem';
import { useLocation, useParams } from 'react-router-dom';
import { getWalletTransactions } from '../../../api/multisigAPI';
import { TransactionType } from '../../../types/transactions';
import { ReactComponent as ArrowsIconGreen } from 'assets/arrows-icon-green.svg';
import { ReactComponent as ArrowsIconWhiteGray } from 'assets/arrows-icon-white-gray.svg';
import { GlobalContext } from '../../../providers/Global';
import { ModalType, SCREEN_LG, SCREEN_SM } from '../../../utils/constants';
import { useMediaQuery } from 'react-responsive';
import { ApplicationRoutes } from 'utils/routes';

const ITEMS_PER_PAGE = 10;

const typeFilters = [
  { label: 'All', value: '' },
  { label: 'Send', value: 'Transfer' },
  { label: 'Token Transfer', value: 'TokenTransfer' },
  { label: 'Contract', value: 'ContractInteraction' },
  { label: 'Associate', value: 'TokenAssociation' },
  { label: 'Threshold', value: 'ThresholdUpdate' },
];

const statusFilters = [
  { label: 'All', value: '' },
  { label: 'Waiting', value: '0' },
  { label: 'Scheduled', value: '6' },
  { label: 'Success', value: '5' },
  { label: 'Expired', value: '3' },
  { label: 'Failed', value: '4' },
];

const TransactionList = () => {
  const location = useLocation();
  const { walletId, multisigAccountId } = useParams();
  const [transactions, setTransactions] = useState<TransactionType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [sortConfig, setSortConfig] = useState<
    { key: string; direction: 'asc' | 'desc' } | undefined
  >(undefined);
  const [activeTypeFilter, setActiveTypeFilter] = useState('');
  const [activeStatusFilter, setActiveStatusFilter] = useState('');
  const { openModal, refreshKey } = useContext(GlobalContext);
  const [changedTxIds, setChangedTxIds] = useState<Set<string>>(new Set());
  const knownTxState = useRef<Map<string, number>>(new Map());
  const highlightTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isScreenSmall = useMediaQuery({ query: `(max-width: ${SCREEN_SM})` });
  const isScreenLarge = useMediaQuery({ query: `(max-width: ${SCREEN_LG})` });

  const isTransactionsPage = location.pathname.includes('transactions');

  const fetchTransactions = useCallback(
    async (
      page: number = 1,
      sort?: { key: string; direction: 'asc' | 'desc' },
      typeFilter?: string,
      statusFilter?: string,
    ) => {
      if (walletId) {
        const isInitialLoad = knownTxState.current.size === 0;
        if (isInitialLoad) {
          setIsLoading(true);
        }
        try {
          const result = await getWalletTransactions(
            walletId,
            page,
            ITEMS_PER_PAGE,
            sort?.key || sortConfig?.key,
            sort?.direction || sortConfig?.direction,
            (typeFilter ?? activeTypeFilter) || undefined,
            (statusFilter ?? activeStatusFilter) || undefined,
            'outgoing',
          );
          if (result?.data) {
            const incoming = result.data.transactions;

            // Detect new transactions and status changes
            if (!isInitialLoad) {
              const changed = new Set<string>();
              incoming.forEach((tx: TransactionType) => {
                const prev = knownTxState.current.get(tx._id);
                if (prev === undefined || prev !== tx.status) {
                  changed.add(tx._id);
                }
              });
              if (changed.size > 0) {
                if (highlightTimer.current) clearTimeout(highlightTimer.current);
                setChangedTxIds(changed);
                highlightTimer.current = setTimeout(() => {
                  setChangedTxIds(new Set());
                  highlightTimer.current = null;
                }, 3300);
              }
            }

            // Update known state
            const newState = new Map<string, number>();
            incoming.forEach((tx: TransactionType) => newState.set(tx._id, tx.status));
            knownTxState.current = newState;

            setTransactions(incoming);
            setTotalPages(result.data.totalPages);
            setTotal(result.data.total);
            setCurrentPage(result.data.page);
          }
        } catch {
          // Network error — keep existing data visible
        } finally {
          setIsLoading(false);
        }
      }
    },
    [walletId, sortConfig, activeTypeFilter, activeStatusFilter],
  );

  useEffect(() => {
    if (walletId) {
      fetchTransactions(1);
    }
  }, [walletId, fetchTransactions, refreshKey]);

  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'desc';
    if (sortConfig?.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    const newSort = { key, direction };
    setSortConfig(newSort);
    fetchTransactions(1, newSort);
  };

  const handleTypeFilter = (value: string) => {
    setActiveTypeFilter(value);
    fetchTransactions(1, sortConfig, value, activeStatusFilter);
  };

  const handleStatusFilter = (value: string) => {
    setActiveStatusFilter(value);
    fetchTransactions(1, sortConfig, activeTypeFilter, value);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      fetchTransactions(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      fetchTransactions(currentPage + 1);
    }
  };

  const activeTypeLabel = typeFilters.find((f) => f.value === activeTypeFilter)?.label || 'All';
  const activeStatusLabel = statusFilters.find((f) => f.value === activeStatusFilter)?.label || 'All';

  const filterButtonStyle = {
    border: '1px solid',
    borderColor: 'blackAlpha.200',
    cursor: 'pointer',
    bg: 'white',
    transition: 'all 0.15s',
    _hover: { borderColor: 'blackAlpha.400' },
  };

  const renderFilterDropdowns = () => (
    <Flex gap={3} mb={2} flexWrap="wrap">
      <ChakraMenu placement="bottom-start">
        <MenuButton
          as={Flex}
          alignItems="center"
          gap={1}
          px={3}
          py={1.5}
          borderRadius={6}
          sx={{ fontSize: '12px' }}
          {...filterButtonStyle}
        >
          <Text as="span" color="blackAlpha.500" fontSize="12px" mr={1}>Type:</Text>
          <Text as="span" fontWeight="600" fontSize="12px">{activeTypeLabel}</Text>
          <Text as="span" fontSize="10px" color="blackAlpha.400" ml={1}>&#9662;</Text>
        </MenuButton>
        <MenuList minW="180px" py={2} borderRadius={8} boxShadow="lg" zIndex={10}>
          {typeFilters.map((filter) => (
            <MenuItem
              key={filter.value}
              fontSize="sm"
              py={2}
              fontWeight={activeTypeFilter === filter.value ? '600' : '400'}
              bg={activeTypeFilter === filter.value ? 'blackAlpha.50' : 'transparent'}
              _hover={{ bg: 'blackAlpha.100' }}
              onClick={() => handleTypeFilter(filter.value)}
            >
              {filter.label}
            </MenuItem>
          ))}
        </MenuList>
      </ChakraMenu>

      <ChakraMenu placement="bottom-start">
        <MenuButton
          as={Flex}
          alignItems="center"
          gap={1}
          px={3}
          py={1.5}
          borderRadius={6}
          sx={{ fontSize: '12px' }}
          {...filterButtonStyle}
        >
          <Text as="span" color="blackAlpha.500" fontSize="12px" mr={1}>Status:</Text>
          <Text as="span" fontWeight="600" fontSize="12px">{activeStatusLabel}</Text>
          <Text as="span" fontSize="10px" color="blackAlpha.400" ml={1}>&#9662;</Text>
        </MenuButton>
        <MenuList minW="180px" py={2} borderRadius={8} boxShadow="lg" zIndex={10}>
          {statusFilters.map((filter) => (
            <MenuItem
              key={filter.value}
              fontSize="sm"
              py={2}
              fontWeight={activeStatusFilter === filter.value ? '600' : '400'}
              bg={activeStatusFilter === filter.value ? 'blackAlpha.50' : 'transparent'}
              _hover={{ bg: 'blackAlpha.100' }}
              onClick={() => handleStatusFilter(filter.value)}
            >
              {filter.label}
            </MenuItem>
          ))}
        </MenuList>
      </ChakraMenu>
    </Flex>
  );

  return total > 0 || (isTransactionsPage && (activeTypeFilter || activeStatusFilter)) ? (
    <Flex
      flex={1}
      p={6}
      pt={isScreenSmall ? 4 : 6}
      bg="white"
      borderRadius={16}
      flexDirection="column"
      boxShadow="0px 1px 2px 0px rgba(0, 0, 0, 0.06), 0px 1px 3px 0px rgba(0, 0, 0, 0.10)"
    >
      {location.pathname.includes('account') && (
        <Text
          size="lg"
          fontWeight={300}
          color="blackAlpha.500"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          Recent Transactions
          {isScreenLarge && (
            <ChakraLink
              as={RouterLink}
              to={`${ApplicationRoutes.Dashboard}/${walletId}/transactions/${multisigAccountId}`}
              cursor="pointer"
              fontSize="sm"
              fontWeight="500"
              letterSpacing="0.5px"
              color="blackAlpha.900"
              textDecoration="none !important"
            >
              View
            </ChakraLink>
          )}
        </Text>
      )}
      {/* Filter dropdowns — only on full Transactions page */}
      {isTransactionsPage && renderFilterDropdowns()}

      <TransactionListHeader onSort={handleSort} sortConfig={sortConfig} />
      <Box display="flex" width="full" flexDirection="column" position="relative">
        {transactions.length > 0 ? (
          transactions.map((transaction: TransactionType) => (
            <OutgoingTransactionItem
              key={transaction._id}
              transaction={transaction}
              highlight={changedTxIds.has(transaction._id)}
            />
          ))
        ) : !isLoading && isTransactionsPage && (activeTypeFilter || activeStatusFilter) ? (
          <Flex flexDirection="column" alignItems="center" py={10} gap={2}>
            <Text size="md" color="blackAlpha.500">
              No transactions match the selected filters.
            </Text>
            <Box
              as="button"
              fontSize="sm"
              color="blackAlpha.700"
              textDecoration="underline"
              onClick={() => {
                setActiveTypeFilter('');
                setActiveStatusFilter('');
                fetchTransactions(1, sortConfig, '', '');
              }}
            >
              Clear filters
            </Box>
          </Flex>
        ) : null}
      </Box>

      {/* Pagination */}
      {totalPages > 1 && (
        <Flex
          justify="space-between"
          align="center"
          mt={4}
          pt={4}
          borderTop="1px solid"
          borderColor="gray.100"
        >
          <Text size="xs" color="blackAlpha.600">
            Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1} -{' '}
            {Math.min(currentPage * ITEMS_PER_PAGE, total)} of {total}
          </Text>
          <Flex gap={2} align="center">
            <Button
              size="sm"
              variant="outline"
              onClick={handlePrevPage}
              isDisabled={currentPage === 1}
              borderRadius={6}
            >
              Previous
            </Button>
            <Text size="sm" fontWeight="500" px={2}>
              {currentPage} / {totalPages}
            </Text>
            <Button
              size="sm"
              variant="outline"
              onClick={handleNextPage}
              isDisabled={currentPage === totalPages}
              borderRadius={6}
            >
              Next
            </Button>
          </Flex>
        </Flex>
      )}
    </Flex>
  ) : !isLoading && isTransactionsPage ? (
    <Flex flexDirection="column" alignItems="center" mt={5}>
      <ArrowsIconGreen />
      <Text size="2xl" variant="bodyBold">
        There are no Transactions yet.
      </Text>
      <Text mt={1} size="xl">
        Create new transaction from the button.
      </Text>
    </Flex>
  ) : !isLoading ? (
    <Flex
      p={6}
      bg="white"
      height="100%"
      borderRadius={16}
      flexDirection="column"
      boxShadow="0px 1px 2px 0px rgba(0, 0, 0, 0.06), 0px 1px 3px 0px rgba(0, 0, 0, 0.10)"
    >
      {location.pathname.includes('account') && (
        <Text size="lg" color="blackAlpha.500" fontWeight={300} mb={6}>
          Recent Transactions
        </Text>
      )}
      <Flex flexDirection="column" alignItems="center" mt={5} gap={8}>
        <ArrowsIconWhiteGray />
        <Flex flexDirection="column" gap={1} alignItems="center">
          <Text size="2xl">There are no Transactions yet.</Text>
          <Text size="sm">Create new transaction from the button below.</Text>
        </Flex>
        <Button
          w={200}
          variant="primary"
          onClick={() => openModal(ModalType.CreateTransactionWizard)}
        >
          New Transaction
        </Button>
      </Flex>
    </Flex>
  ) : (
    <Box textAlign="center" mt="120px">
      <Spinner size="lg" />
    </Box>
  );
};

export default TransactionList;
